import { PACKAGE_LIMITS, type SavedPackage } from './manifest';
export interface PackagePersistence {
  list(): Promise<SavedPackage[]>;
  put(record: SavedPackage): Promise<void>;
  remove(id: string): Promise<void>;
}
export class BrowserPackagePersistence implements PackagePersistence {
  private database?: Promise<IDBDatabase>;
  private open() {
    return (this.database ??= new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open('digitiles-extension-packages', 1);
      request.onupgradeneeded = () =>
        request.result.createObjectStore('packages', { keyPath: 'manifest.id' });
      request.onerror = () => {
        this.database = undefined;
        reject(request.error);
      };
      request.onblocked = () =>
        reject(new Error('Close other Digitiles tabs to update extension storage.'));
      request.onsuccess = () => {
        request.result.onversionchange = () => {
          request.result.close();
          this.database = undefined;
        };
        resolve(request.result);
      };
    }));
  }
  private async transaction<T>(
    mode: IDBTransactionMode,
    action: (store: IDBObjectStore) => IDBRequest<T>,
  ): Promise<T> {
    const database = await this.open();
    return new Promise((resolve, reject) => {
      const transaction = database.transaction('packages', mode);
      const request = action(transaction.objectStore('packages'));
      transaction.oncomplete = () => resolve(request.result);
      transaction.onerror = transaction.onabort = () =>
        reject(
          transaction.error ?? request.error ?? new Error('Extension storage failed.'),
        );
    });
  }
  list() {
    return this.transaction<SavedPackage[]>('readonly', (store) => store.getAll());
  }
  async put(record: SavedPackage) {
    const database = await this.open();
    await new Promise<void>((resolve, reject) => {
      const transaction = database.transaction('packages', 'readwrite');
      const store = transaction.objectStore('packages');
      const keys = store.getAllKeys();
      let limitError: Error | undefined;
      keys.onsuccess = () => {
        if (
          !keys.result.includes(record.manifest.id) &&
          keys.result.length >= PACKAGE_LIMITS.installed
        ) {
          limitError = new Error('Remove a package before installing more (limit 16).');
          transaction.abort();
          return;
        }
        store.put(record);
      };
      transaction.oncomplete = () => resolve();
      transaction.onerror = transaction.onabort = () =>
        reject(limitError ?? transaction.error ?? new Error('Extension storage failed.'));
    });
  }
  async remove(id: string) {
    await this.transaction('readwrite', (store) => store.delete(id));
  }
}
